import React, { useState, useEffect, useRef } from 'react';
import './CommunicationsCenter.css';
import {
  Container,
  Row,
  Col,
  Card,
  Nav,
  Tab,
  Badge,
  Button,
  Form,
  InputGroup,
  ListGroup,
  Alert,
  Spinner,
  Dropdown,
  Modal,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComments,
  faPhone,
  faCog,
  faSearch,
  faPaperPlane,
  faFilter,
  faSync,
  faExclamationCircle,
  faCheckCircle,
  faUser,
  faBuilding,
  faBell,
  faForward,
  faReply,
  faTrash,
  faArchive,
  faEllipsisV,
  faPhoneAlt,
  faEnvelope,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import toast from 'react-hot-toast';
import moment from 'moment';
import PhoneNumberManagement from 'components/modules/communications/PhoneNumberManagement';

interface Message {
  id: string;
  message_sid: string;
  from_number: string;
  to_number: string;
  body: string;
  direction: 'inbound' | 'outbound';
  status: string;
  created_at: string;
  lead_id?: string;
  user_id?: string;
}

interface Conversation {
  phoneNumber: string;
  messages: Message[];
  lastMessage: Message;
  unreadCount: number;
  contactName?: string;
  leadId?: string;
}

const CommunicationsCenter: React.FC = () => {
  const [activeTab, setActiveTab] = useState('messages');
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDirection, setFilterDirection] = useState<'all' | 'inbound' | 'outbound'>('all');
  const [newMessage, setNewMessage] = useState('');
  const [replyNumber, setReplyNumber] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [showForwardingModal, setShowForwardingModal] = useState(false);
  const [forwardingNumber, setForwardingNumber] = useState('');
  const [corporateNumber, setCorporateNumber] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const refreshInterval = useRef<NodeJS.Timeout>();

  // Fetch messages
  const fetchMessages = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      else setRefreshing(true);

      const response = await axiosInstance.get('/twilio/messages', {
        params: {
          limit: 100,
          direction: filterDirection === 'all' ? undefined : filterDirection
        }
      });

      if (response.data.success) {
        setMessages(response.data.messages || []);
        processConversations(response.data.messages || []);
      }
    } catch (error: any) {
      console.error('Error fetching messages:', error);
      if (!showLoading) {
        toast.error('Failed to refresh messages');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Process messages into conversations
  const processConversations = (messageList: Message[]) => {
    const convMap = new Map<string, Conversation>();

    messageList.forEach((msg) => {
      // Group by external phone number (not our numbers)
      const phoneNumber = msg.direction === 'inbound' ? msg.from_number : msg.to_number;
      
      if (!convMap.has(phoneNumber)) {
        convMap.set(phoneNumber, {
          phoneNumber,
          messages: [],
          lastMessage: msg,
          unreadCount: 0
        });
      }

      const conv = convMap.get(phoneNumber)!;
      conv.messages.push(msg);
      
      // Update last message if this one is newer
      if (new Date(msg.created_at) > new Date(conv.lastMessage.created_at)) {
        conv.lastMessage = msg;
      }

      // Count unread inbound messages
      if (msg.direction === 'inbound' && msg.status !== 'read') {
        conv.unreadCount++;
      }
    });

    // Sort conversations by last message time
    const sortedConversations = Array.from(convMap.values()).sort(
      (a, b) => new Date(b.lastMessage.created_at).getTime() - new Date(a.lastMessage.created_at).getTime()
    );

    setConversations(sortedConversations);
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const response = await axiosInstance.get('/twilio/messages/unread-count');
      if (response.data.success) {
        setUnreadCount(response.data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Get corporate numbers
  const fetchCorporateNumbers = async () => {
    try {
      const response = await axiosInstance.get('/twilio/corporate-numbers');
      if (response.data.success && response.data.corporate_numbers?.general_notifications) {
        setCorporateNumber(response.data.corporate_numbers.general_notifications);
      }
    } catch (error) {
      console.error('Error fetching corporate numbers:', error);
    }
  };

  // Mark message as read
  const markAsRead = async (messageId: string) => {
    try {
      await axiosInstance.put(`/twilio/messages/${messageId}/read`);
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  // Send SMS
  const sendMessage = async () => {
    if (!replyNumber || !newMessage.trim()) {
      toast.error('Please enter a phone number and message');
      return;
    }

    setSending(true);
    try {
      const response = await axiosInstance.post('/twilio/send-sms', {
        to_number: replyNumber,
        message: newMessage,
        message_type: 'general'
      });

      if (response.data.success) {
        toast.success('Message sent successfully!');
        setNewMessage('');
        
        // Refresh messages after short delay
        setTimeout(() => fetchMessages(false), 1000);
      } else {
        toast.error(response.data.error || 'Failed to send message');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Save forwarding preferences
  const saveForwardingSettings = async () => {
    try {
      // This would save to user preferences in the database
      // For now, we'll use localStorage
      localStorage.setItem('sms_forwarding_number', forwardingNumber);
      toast.success('Forwarding settings saved');
      setShowForwardingModal(false);
    } catch (error) {
      toast.error('Failed to save forwarding settings');
    }
  };

  // Format phone number for display
  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{1})?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `${match[1] ? '+' + match[1] + ' ' : ''}(${match[2]}) ${match[3]}-${match[4]}`;
    }
    return phone;
  };

  // Auto-refresh setup
  useEffect(() => {
    fetchMessages();
    fetchUnreadCount();
    fetchCorporateNumbers();

    // Set up auto-refresh every 30 seconds
    refreshInterval.current = setInterval(() => {
      fetchMessages(false);
      fetchUnreadCount();
    }, 30000);

    // Load forwarding settings
    const savedForwarding = localStorage.getItem('sms_forwarding_number');
    if (savedForwarding) {
      setForwardingNumber(savedForwarding);
    }

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [filterDirection]);

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedConversation?.messages]);

  // Filter conversations based on search
  const filteredConversations = conversations.filter(conv => 
    conv.phoneNumber.includes(searchTerm) ||
    conv.lastMessage.body.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container fluid className="communications-center py-4">
      <Row className="mb-4">
        <Col>
          <h2>
            <FontAwesomeIcon icon={faComments} className="me-3" />
            Communications Center
            {unreadCount > 0 && (
              <Badge bg="danger" className="ms-3">{unreadCount} New</Badge>
            )}
          </h2>
          <p className="text-muted">Manage all your text messages and phone communications in one place</p>
        </Col>
        <Col xs="auto">
          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={() => fetchMessages(false)}
            disabled={refreshing}
          >
            <FontAwesomeIcon icon={faSync} spin={refreshing} className="me-2" />
            Refresh
          </Button>
        </Col>
      </Row>

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'messages')}>
        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link eventKey="messages">
              <FontAwesomeIcon icon={faComments} className="me-2" />
              Messages
              {unreadCount > 0 && <Badge bg="danger" className="ms-2">{unreadCount}</Badge>}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="conversations">
              <FontAwesomeIcon icon={faUser} className="me-2" />
              Conversations
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="phone">
              <FontAwesomeIcon icon={faPhone} className="me-2" />
              Phone Management
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="settings">
              <FontAwesomeIcon icon={faCog} className="me-2" />
              Settings
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* Messages Tab - All messages in chronological order */}
          <Tab.Pane eventKey="messages">
            <Card>
              <Card.Header>
                <Row className="align-items-center">
                  <Col>
                    <InputGroup>
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faSearch} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search messages..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col xs="auto">
                    <Dropdown>
                      <Dropdown.Toggle variant="outline-secondary" size="sm">
                        <FontAwesomeIcon icon={faFilter} className="me-2" />
                        {filterDirection === 'all' ? 'All' : filterDirection === 'inbound' ? 'Received' : 'Sent'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setFilterDirection('all')}>All Messages</Dropdown.Item>
                        <Dropdown.Item onClick={() => setFilterDirection('inbound')}>Received</Dropdown.Item>
                        <Dropdown.Item onClick={() => setFilterDirection('outbound')}>Sent</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Loading messages...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <Alert variant="info">
                    <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
                    No messages found. Messages sent to {corporateNumber || 'your number'} will appear here.
                  </Alert>
                ) : (
                  <ListGroup variant="flush">
                    {messages.map((message) => (
                      <ListGroup.Item 
                        key={message.id}
                        className={`border-0 ${message.direction === 'inbound' && message.status !== 'read' ? 'bg-light' : ''}`}
                        onClick={() => {
                          if (message.direction === 'inbound' && message.status !== 'read') {
                            markAsRead(message.id);
                          }
                        }}
                      >
                        <Row>
                          <Col xs="auto">
                            <div className={`rounded-circle p-2 ${message.direction === 'inbound' ? 'bg-primary' : 'bg-success'} text-white`}>
                              <FontAwesomeIcon icon={message.direction === 'inbound' ? faUser : faBuilding} />
                            </div>
                          </Col>
                          <Col>
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <strong>{message.direction === 'inbound' ? 'From: ' : 'To: '}</strong>
                                {formatPhoneNumber(message.direction === 'inbound' ? message.from_number : message.to_number)}
                                {message.direction === 'inbound' && message.status !== 'read' && (
                                  <Badge bg="danger" className="ms-2">New</Badge>
                                )}
                              </div>
                              <small className="text-muted">
                                {moment(message.created_at).fromNow()}
                              </small>
                            </div>
                            <p className="mb-1 mt-2">{message.body}</p>
                            {message.direction === 'inbound' && (
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setReplyNumber(message.from_number);
                                  setActiveTab('messages');
                                  window.scrollTo(0, document.body.scrollHeight);
                                }}
                              >
                                <FontAwesomeIcon icon={faReply} className="me-1" />
                                Reply
                              </Button>
                            )}
                          </Col>
                        </Row>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Card.Body>
              <Card.Footer>
                <Form onSubmit={(e) => { e.preventDefault(); sendMessage(); }}>
                  <Row>
                    <Col md={3}>
                      <Form.Control
                        type="tel"
                        placeholder="+1 (555) 555-5555"
                        value={replyNumber}
                        onChange={(e) => setReplyNumber(e.target.value)}
                        disabled={sending}
                      />
                    </Col>
                    <Col>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          placeholder="Type your message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          disabled={sending}
                          maxLength={160}
                        />
                        <Button 
                          variant="primary" 
                          type="submit"
                          disabled={sending || !replyNumber || !newMessage.trim()}
                        >
                          <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                          Send
                        </Button>
                      </InputGroup>
                      <small className="text-muted">{160 - newMessage.length} characters remaining</small>
                    </Col>
                  </Row>
                </Form>
              </Card.Footer>
            </Card>
          </Tab.Pane>

          {/* Conversations Tab - Grouped by contact */}
          <Tab.Pane eventKey="conversations">
            <Row>
              <Col md={4}>
                <Card>
                  <Card.Header>
                    <InputGroup size="sm">
                      <InputGroup.Text>
                        <FontAwesomeIcon icon={faSearch} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search conversations..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Card.Header>
                  <ListGroup variant="flush" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {filteredConversations.map((conv) => (
                      <ListGroup.Item
                        key={conv.phoneNumber}
                        action
                        active={selectedConversation?.phoneNumber === conv.phoneNumber}
                        onClick={() => setSelectedConversation(conv)}
                        className="border-0"
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <h6 className="mb-1">
                              {formatPhoneNumber(conv.phoneNumber)}
                              {conv.unreadCount > 0 && (
                                <Badge bg="danger" className="ms-2">{conv.unreadCount}</Badge>
                              )}
                            </h6>
                            <p className="mb-1 text-truncate small text-muted">
                              {conv.lastMessage.body}
                            </p>
                            <small className="text-muted">
                              {moment(conv.lastMessage.created_at).fromNow()}
                            </small>
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card>
              </Col>
              <Col md={8}>
                {selectedConversation ? (
                  <Card>
                    <Card.Header>
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">{formatPhoneNumber(selectedConversation.phoneNumber)}</h5>
                        <div>
                          <Button variant="outline-secondary" size="sm" className="me-2">
                            <FontAwesomeIcon icon={faPhoneAlt} />
                          </Button>
                          <Button variant="outline-secondary" size="sm">
                            <FontAwesomeIcon icon={faEllipsisV} />
                          </Button>
                        </div>
                      </div>
                    </Card.Header>
                    <Card.Body style={{ height: '500px', overflowY: 'auto' }}>
                      <div className="messages-container">
                        {selectedConversation.messages
                          .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                          .map((msg) => (
                            <div
                              key={msg.id}
                              className={`mb-3 ${msg.direction === 'outbound' ? 'text-end' : 'text-start'}`}
                            >
                              <div
                                className={`d-inline-block p-3 rounded-3 ${
                                  msg.direction === 'outbound' 
                                    ? 'bg-primary text-white' 
                                    : 'bg-light text-dark'
                                }`}
                                style={{ maxWidth: '70%' }}
                              >
                                <p className="mb-1">{msg.body}</p>
                                <small className={msg.direction === 'outbound' ? 'text-white-50' : 'text-muted'}>
                                  {moment(msg.created_at).format('MMM D, h:mm A')}
                                </small>
                              </div>
                            </div>
                          ))}
                        <div ref={messagesEndRef} />
                      </div>
                    </Card.Body>
                    <Card.Footer>
                      <Form onSubmit={(e) => { 
                        e.preventDefault(); 
                        setReplyNumber(selectedConversation.phoneNumber);
                        sendMessage(); 
                      }}>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            placeholder="Type your message..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            disabled={sending}
                          />
                          <Button 
                            variant="primary" 
                            type="submit"
                            disabled={sending || !newMessage.trim()}
                          >
                            <FontAwesomeIcon icon={faPaperPlane} />
                          </Button>
                        </InputGroup>
                      </Form>
                    </Card.Footer>
                  </Card>
                ) : (
                  <Card>
                    <Card.Body className="text-center py-5">
                      <FontAwesomeIcon icon={faComments} size="3x" className="text-muted mb-3" />
                      <h5>Select a conversation</h5>
                      <p className="text-muted">Choose a conversation from the list to view messages</p>
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>
          </Tab.Pane>

          {/* Phone Management Tab */}
          <Tab.Pane eventKey="phone">
            <PhoneNumberManagement />
          </Tab.Pane>

          {/* Settings Tab */}
          <Tab.Pane eventKey="settings">
            <Card>
              <Card.Header>
                <h5>Communication Settings</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <h6>Text Forwarding</h6>
                    <p className="text-muted">Forward incoming texts to another number</p>
                    <InputGroup className="mb-3">
                      <Form.Control
                        type="tel"
                        placeholder="Forwarding number"
                        value={forwardingNumber}
                        onChange={(e) => setForwardingNumber(e.target.value)}
                      />
                      <Button 
                        variant="primary"
                        onClick={() => setShowForwardingModal(true)}
                      >
                        <FontAwesomeIcon icon={faForward} className="me-2" />
                        Update
                      </Button>
                    </InputGroup>
                    {forwardingNumber && (
                      <Alert variant="info">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                        Texts are being forwarded to {formatPhoneNumber(forwardingNumber)}
                      </Alert>
                    )}
                  </Col>
                  <Col md={6}>
                    <h6>Notification Preferences</h6>
                    <Form>
                      <Form.Check 
                        type="switch"
                        id="email-notifications"
                        label="Email notifications for new messages"
                        className="mb-2"
                      />
                      <Form.Check 
                        type="switch"
                        id="browser-notifications"
                        label="Browser notifications"
                        className="mb-2"
                      />
                      <Form.Check 
                        type="switch"
                        id="sound-notifications"
                        label="Sound alerts"
                      />
                    </Form>
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col>
                    <h6>Corporate Number</h6>
                    <p className="text-muted">
                      Your main business number: <strong>{formatPhoneNumber(corporateNumber || '602-860-6110')}</strong>
                    </p>
                    <Alert variant="warning">
                      <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
                      <strong>For Rent Sign Texts:</strong> All texts to 602-860-6110 will appear here and can be forwarded to your personal number.
                    </Alert>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Forwarding Settings Modal */}
      <Modal show={showForwardingModal} onHide={() => setShowForwardingModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Update Forwarding Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Forward incoming texts to:</Form.Label>
              <Form.Control
                type="tel"
                placeholder="+1 (555) 555-5555"
                value={forwardingNumber}
                onChange={(e) => setForwardingNumber(e.target.value)}
              />
              <Form.Text className="text-muted">
                Enter the phone number where you want to receive forwarded texts
              </Form.Text>
            </Form.Group>
            <Alert variant="info">
              <FontAwesomeIcon icon={faBell} className="me-2" />
              You'll receive forwarded texts from both your assigned number and the corporate number (602-860-6110)
            </Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForwardingModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveForwardingSettings}>
            Save Settings
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CommunicationsCenter;